import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, DollarSign, MapPin } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";

export default function GoogleAdsMiamiPage() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Google Ads en Miami",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": { "@type": "City", "name": "Miami", "addressRegion": "FL" }
  };

  return (
    <>
      <Helmet>
        <html lang="es" />
        <title>Google Ads en Miami, FL | Campañas Bilingües – Luis ROC</title>
        <meta name="description" content="Campañas de Google Ads optimizadas en Miami. Estrategia PPC bilingüe para mercado hispano con medición de CPA, ROAS y conversiones." />
        <link rel="canonical" href="https://luisroc.com/google-ads-miami" />
      </Helmet>

      <Header language="es" onLanguageChange={() => {}} />
      
      <main className="min-h-screen bg-background">
        <BreadcrumbNav 
          items={[
            { name: "Servicios", url: "/servicios" },
            { name: "Florida", url: "/servicios-locales-florida" },
            { name: "Google Ads Miami", url: "/google-ads-miami" }
          ]}
          language="es"
        />

        <section className="bg-gradient-to-br from-primary/10 via-background to-background py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <MapPin className="w-6 h-6 text-primary" />
                <span className="text-lg font-semibold text-primary">Miami, Florida</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Google Ads en Miami — Campañas orientadas a demanda local
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Estrategia PPC bilingüe optimizada para el mercado hispano de Miami, con medición orientada a negocio.
              </p>
              <Button asChild size="lg" className="whitespace-normal h-auto py-3 text-center">
                <a href="/contacto">
                  Agenda tu reunión inicial gratuita de 30 minutos
                </a>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CheckCircle className="w-8 h-8 text-primary mb-2" />
                  <CardTitle>Campañas Bilingües</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Anuncios en español e inglés con segmentación precisa por idioma y ubicación en Miami.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CheckCircle className="w-8 h-8 text-primary mb-2" />
                  <CardTitle>Segmentación Local</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Targeting por zona: Brickell, Coral Gables, Wynwood, con ajustes de pujas por área.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CheckCircle className="w-8 h-8 text-primary mb-2" />
                  <CardTitle>Medición orientada a negocio</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Tracking completo: CPA, ROAS, conversiones por campaña con reportes mensuales.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary/5">
          <div className="container mx-auto px-4 text-center">
            <DollarSign className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">¿Listo para Crecer en Miami?</h2>
            <Button asChild size="lg" className="whitespace-normal h-auto py-3 text-center">
              <a href="/contacto">
                Agenda tu reunión inicial gratuita de 30 minutos
              </a>
            </Button>
          </div>
        </section>
      </main>

        <KnowledgeConnections path="/google-ads-miami" language="es" />

      <Footer language="es" />
      <JsonLd data={serviceSchema} />
    </>
  );
}
